import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { SubscriptionsApiService, SubscriptionPlan } from 'shared/services/api/subscriptions.api.service';
import { SnackbarService } from 'shared/services/snackbar.service';
import { take, finalize } from 'rxjs';
import { APIResponse } from 'fakturnia-shared';

export interface DialogData {
  plan: SubscriptionPlan | null;
  mode: 'create' | 'edit';
}

@Component({
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatProgressSpinnerModule
  ],
  selector: 'app-subscription-plan-dialog',
  templateUrl: './subscription-plan-dialog.component.html',
  styleUrls: ['./subscription-plan-dialog.component.scss']
})
export class SubscriptionPlanDialogComponent implements OnInit {
  form: FormGroup;
  isSaving = false;
  isDeleting = false;

  planStages = [0, 1, 2, 3];

  constructor(
    private _fb: FormBuilder,
    private _dialogRef: MatDialogRef<SubscriptionPlanDialogComponent>,
    private _subscriptionsApi: SubscriptionsApiService,
    private _snackbar: SnackbarService,
    @Inject(MAT_DIALOG_DATA) public data: DialogData
  ) {}

  ngOnInit(): void {
    this._buildForm();

    if (this.data.plan) {
      this._populateForm(this.data.plan);
    }
  }

  private _buildForm() {
    this.form = this._fb.group({
      stage: [1, Validators.required],
      name: ['', Validators.required],
      description: [''],
      price: [0, [Validators.required, Validators.min(0)]],
      originalPrice: [null],
      discountPercent: [{ value: null, disabled: true }], // Tylko do odczytu - przeliczany automatycznie
      durationMonths: [12, [Validators.required, Validators.min(1)]],
      durationYears: [null],
      isActive: [true],
      invoiceLimit: [null]
    });

    // Subskrybuj zmiany cen i przeliczaj rabat
    this.form.get('price')?.valueChanges.subscribe(() => this._calculateDiscount());
    this.form.get('originalPrice')?.valueChanges.subscribe(() => this._calculateDiscount());
  }

  private _calculateDiscount() {
    const price = this.form.get('price')?.value;
    const originalPrice = this.form.get('originalPrice')?.value;

    if (originalPrice && originalPrice > 0 && price !== null && price >= 0) {
      // Rabat = ((cenaOryginalna - cenaPoCenie) / cenaOryginalna) * 100
      // Zaokrąglamy w dół do liczby całkowitej
      const discount = Math.floor(((originalPrice - price) / originalPrice) * 100);
      this.form.get('discountPercent')?.setValue(discount > 0 ? discount : null, { emitEvent: false });
    } else {
      this.form.get('discountPercent')?.setValue(null, { emitEvent: false });
    }
  }

  private _populateForm(plan: SubscriptionPlan) {
    this.form.patchValue({
      stage: plan.stage,
      name: plan.name,
      description: plan.description || '',
      price: plan.price / 100, // Konwersja z groszy na złotówki
      originalPrice: plan.originalPrice ? plan.originalPrice / 100 : null,
      durationMonths: plan.durationMonths,
      durationYears: plan.durationYears || null,
      isActive: plan.isActive,
      invoiceLimit: plan.invoiceLimit || null
    });
    // Przelicz rabat po załadowaniu danych
    this._calculateDiscount();
  }

  onSubmit() {
    if (this.form.invalid) {
      return;
    }

    // Używamy getRawValue() żeby pobrać też disabled pola (discountPercent)
    const formValue = this.form.getRawValue();
    
    const planData: Partial<SubscriptionPlan> = {
      stage: formValue.stage,
      name: formValue.name,
      description: formValue.description || '',
      price: Math.round(formValue.price * 100), // Konwersja na grosze
      originalPrice: formValue.originalPrice ? Math.round(formValue.originalPrice * 100) : null,
      discountPercent: formValue.discountPercent || null,
      durationMonths: formValue.durationMonths,
      durationYears: formValue.durationYears || null,
      isActive: formValue.isActive,
      invoiceLimit: formValue.invoiceLimit || null
    };

    this.isSaving = true;

    const request$ = this.data.mode === 'edit' && this.data.plan
      ? this._subscriptionsApi.updatePlan(this.data.plan._id || this.data.plan.id!, planData)
      : this._subscriptionsApi.createPlan(planData);

    request$
      .pipe(
        take(1),
        finalize(() => this.isSaving = false)
      )
      .subscribe({
        next: (response: APIResponse) => {
          if (response.success) {
            this._snackbar.showSnackBar(
              this.data.mode === 'edit' ? 'Plan został zaktualizowany' : 'Plan został utworzony',
              'success'
            );
            this._dialogRef.close(true);
          } else {
            this._snackbar.showSnackBar(response.message || 'Wystąpił błąd', 'warning');
          }
        },
        error: () => {
          this._snackbar.showSnackBar('Wystąpił błąd podczas zapisywania', 'warning');
        }
      });
  }

  deletePlan() {
    if (!this.data.plan) return;

    const planId = this.data.plan._id || this.data.plan.id;
    if (!planId) return;

    if (!confirm('Czy na pewno chcesz usunąć ten plan?')) return;

    this.isDeleting = true;

    this._subscriptionsApi.deletePlan(planId)
      .pipe(
        take(1),
        finalize(() => this.isDeleting = false)
      )
      .subscribe({
        next: (response: APIResponse) => {
          if (response.success) {
            this._snackbar.showSnackBar('Plan został usunięty', 'success');
            this._dialogRef.close(true);
          } else {
            this._snackbar.showSnackBar(response.message || 'Wystąpił błąd', 'warning');
          }
        },
        error: () => {
          this._snackbar.showSnackBar('Wystąpił błąd podczas usuwania', 'warning');
        }
      });
  }

  close() {
    this._dialogRef.close(false);
  }

  get isEditMode(): boolean {
    return this.data.mode === 'edit';
  }
}
